import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguageState] = useState("en");

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUserState(null);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Adaugă endpoint pentru subscription info
      const subResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/subscriptions/status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setUserState({
        ...response.data.user,
        token,
        is_admin: response.data.user.is_admin || false,
        subscription: {
          tier: subResponse.data.tier || "trial",
          status: subResponse.data.status || "trial",
          end_date: subResponse.data.end_date,
          days_remaining: subResponse.data.days_remaining || 2,
          isTrial: subResponse.data.tier === "trial" || false,
        },
      });

      if (response.data.user?.language) {
        setLanguageState(response.data.user.language);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      localStorage.removeItem("token");
      setUserState(null);
    } finally {
      setLoading(false);
    }
  };

  // Adaugă funcție pentru upgrade subscription
  const upgradeSubscription = async (planId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/subscriptions/upgrade-tier`,
        { planId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Re-fetch user data după upgrade
      await fetchUserData();
      return response.data;
    } catch (error) {
      console.error("Upgrade error:", error);
      throw error;
    }
  };

  // Actualizează UserContext.Provider să includă upgradeSubscription

  const login = async (token, userData = {}) => {
    localStorage.setItem("token", token);

    setUserState((prev) => ({
      ...prev,
      ...userData,
      token,
      is_admin: userData.is_admin || false,
    }));

    if (userData.language) {
      setLanguageState(userData.language);
    }

    await fetchUserData();
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      setUserState(null);
    }
  };

  const setLanguage = (newLanguage) => {
    setLanguageState(newLanguage);
    if (user) {
      setUserState((prev) => ({
        ...prev,
        language: newLanguage,
      }));
    }
  };

  const saveLanguageToBackend = async (newLanguage) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/user/language`,
          { language: newLanguage },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setLanguageState(newLanguage);
        if (user) {
          setUserState((prev) => ({
            ...prev,
            language: newLanguage,
          }));
        }
        return true;
      }
    } catch (error) {
      console.error("Error saving language to backend:", error);
      throw error;
    }
    return false;
  };

  const updateUser = (updates) => {
    setUserState((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const isAdmin = () => {
    return user?.is_admin === true;
  };

  useEffect(() => {
    fetchUserData();

    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && ["en", "ro", "de"].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: updateUser,
        fetchUserData,
        login,
        logout,
        loading,
        language,
        setLanguage,
        saveLanguageToBackend,
        isAdmin,
        upgradeSubscription,
      }}
    >
      {!loading && children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
